export type ConversationStatus = 'open' | 'waiting' | 'resolved';
export type ConversationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type ConversationCategory = 'question' | 'bug' | 'feature' | 'billing' | 'other';
export type TaskType = 'bug' | 'feature' | 'issue' | 'task';
export type TaskStatus = 'backlog' | 'todo' | 'in_progress' | 'review' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export const TASK_STATUSES: TaskStatus[] = ['backlog', 'todo', 'in_progress', 'review', 'done'];
export const TASK_TYPES: TaskType[] = ['bug', 'feature', 'issue', 'task'];
export const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high', 'urgent'];
export const CONVERSATION_CATEGORIES: ConversationCategory[] = ['question', 'bug', 'feature', 'billing', 'other'];
export const CONVERSATION_PRIORITIES: ConversationPriority[] = ['low', 'normal', 'high', 'urgent'];

/** A member of the Ardealy support team (a super admin). */
export interface StaffMember {
  email: string;
  name: string;
}

export interface SupportAttachment {
  url: string;
  name: string;
  mimeType: string;
  size: number;
  kind: 'image' | 'video' | 'file';
}

export interface SupportPerson {
  email?: string;
  name?: string;
  userId?: string;
}

export interface SupportSystemEvent {
  type: 'task_linked' | 'task_status' | 'status_changed';
  task?: string;
  data?: { number?: number; type?: TaskType; title?: string; status?: string; from?: string; by?: string };
}

export interface SupportMessage {
  _id: string;
  conversation: string;
  authorKind: 'vendor' | 'support' | 'system';
  author?: SupportPerson;
  body: string;
  attachments: SupportAttachment[];
  system?: SupportSystemEvent;
  /** Staff only. */
  internal?: boolean;
  createdAt: string;
  /** Client-side: still sending. */
  pending?: boolean;
}

export interface TaskSummary {
  _id: string;
  number: number;
  type: TaskType;
  title: string;
  status: TaskStatus;
}

/** A conversation as the vendor sees it. */
export interface VendorConversation {
  _id: string;
  subject: string;
  status: ConversationStatus;
  category: ConversationCategory;
  createdBy: SupportPerson;
  createdAt: string;
  lastMessageAt: string;
  lastMessagePreview?: string;
  lastMessageBy?: 'vendor' | 'support';
  unread: number;
  tasks: TaskSummary[];
}

export interface SupportContext {
  page?: string;
  url?: string;
  appVersion?: string;
  userAgent?: string;
  viewport?: string;
  locale?: string;
}

/** A conversation as staff see it. */
export interface StaffConversation {
  _id: string;
  vendor: string;
  vendorName?: string;
  createdBy: SupportPerson;
  subject: string;
  status: ConversationStatus;
  priority: ConversationPriority;
  category: ConversationCategory;
  assignee?: string;
  lastMessageAt: string;
  lastMessagePreview?: string;
  lastMessageBy?: 'vendor' | 'support';
  messageCount: number;
  vendorUnread: number;
  supportUnread: number;
  context?: SupportContext;
  tasks: string[];
  resolvedAt?: string;
  createdAt: string;
}

export interface InboxCounts {
  open: number;
  waiting: number;
  resolved: number;
  unread: number;
  all: number;
}

export interface SupportTask {
  _id: string;
  number: number;
  type: TaskType;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  order: number;
  vendor?: string;
  vendorName?: string;
  conversations: string[];
  assignee?: string;
  labels: string[];
  visibleToVendor: boolean;
  createdBy: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  linkedConversations?: { _id: string; subject: string; status: ConversationStatus; vendorName?: string; lastMessageAt: string }[];
}

export interface StaffConversationDetail {
  conversation: StaffConversation;
  messages: SupportMessage[];
  tasks: SupportTask[];
  vendor: {
    _id: string;
    name: string;
    alias?: string;
    email?: string;
    storeUrl?: string;
    createdAt?: string;
    planName?: string;
    seats: { limit: number; inUse: number | null };
  } | null;
  history: { _id: string; subject: string; status: ConversationStatus; lastMessageAt: string }[];
}
